/* eslint-disable no-nested-ternary */
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Custom hook for managing time input state and handlers
 *
 * @file useTimeInput.js
 * @author Satyaki Bose
 */

import { useState, useEffect, useRef } from 'react';
import { TIMEFIELD_CONSTANTS } from '../common/constants';
import { parse24Hour, convertTo24Hour, validateHour, validateMinute } from '../common/dateTimeUtils';

const {
  DEFAULT_PERIOD,
  HOUR_MIN,
  HOUR_MAX,
  MINUTE_MIN,
  MINUTE_MAX,
  MAX_INPUT_LENGTH,
  FIELD_TYPES,
  PERIOD_VALUES,
  KEY_CODES
} = TIMEFIELD_CONSTANTS;



/**
 * Custom hook for managing time input state and handlers
 * @param {Object} params - Parameters object
 * @param {string} params.value - Current time value in 24-hour format
 * @param {Function} params.onChange - Callback function when time changes
 * @param {string} params.periodButtonClassName - CSS class name for period button (for focus)
 * @returns {Object} Time input state and handlers
 */
const useTimeInput = ({ value, onChange }) => {
  // Initialize state: if value exists, use it; else show blank state
  const getInitialState = () => {
    if (value && value.includes(':')) {
      const parsed = parse24Hour(value);

      return {
        hour: parsed.hour,
        minute: parsed.minute,
        period: parsed.period
      };
    }

    return {
      hour: '',
      minute: '',
      period: DEFAULT_PERIOD
    };
  };

  const initialState = getInitialState();
  const [hour, setHour] = useState(initialState.hour);
  const [minute, setMinute] = useState(initialState.minute);
  const [period, setPeriod] = useState(initialState.period);
  const [activeField, setActiveField] = useState(null);
  const [isHourFocused, setIsHourFocused] = useState(false);
  const [isMinuteFocused, setIsMinuteFocused] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [isPeriodFocused, setIsPeriodFocused] = useState(false);
  const [isComponentActive, setIsComponentActive] = useState(false);

  const hourRef = useRef(null);
  const minuteRef = useRef(null);
  const periodRef = useRef(null);
  const onChangeRef = useRef(onChange); // stores the latest onChange callback
  const previousValueRef = useRef(value); // stores the previous value prop
  const isInternalSyncRef = useRef(false); // flag to prevent onChange from being called during internal sync

  /**
   * this fixes edge case, sometimes in current day with only time change,
   * the callback doesnt fire due to internal sync, we cancel it if user interaction
   */
  const cancelInternalSync = () => {
    if (isInternalSyncRef.current) {
      isInternalSyncRef.current = false;
    }
  };

  /**
     * Effect 1: Keep onChange callback ref updated
     * Updates the onChangeRef without triggering any side effects.
     * This allows us to always call the latest onChange callback without
     * including it in dependency arrays (which would cause re-renders).
     */
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  /**
     * Effect 2: Sync internal state from value prop
     * When the value prop changes, update the internal hour/minute/period state
     * to match it. Only syncs when user is not actively editing the fields.
     * If value is empty, sets hour/minute to blank to show "--:--" placeholder.
     * Marks the operation as an internal sync to prevent triggering onChange.
     */
  useEffect(() => {
    // Skip if we're currently editing
    if (isComponentActive) {
      return;
    }

    // Skip if value hasn't actually changed
    if (value === previousValueRef.current) {
      return;
    }

    // Mark that we're doing an internal sync
    isInternalSyncRef.current = true;
    previousValueRef.current = value;

    // If value exists, parse it; else show blank state
    if (value && value.includes(':')) {
      const parsed = parse24Hour(value);
      setHour(parsed.hour);
      setMinute(parsed.minute);
      setPeriod(parsed.period);
    } else {
      // Show blank state when value is empty
      setHour('');
      setMinute('');
      setPeriod(DEFAULT_PERIOD);
    }
  }, [value, isComponentActive]);

  /**
     * Effect 3: Reset internal sync flag after state updates complete
     * After syncing from props (Effect 2), this effect waits for the state
     * updates to complete, then resets the sync flag. This ensures we don't
     * trigger onChange during prop-based syncs. The flag is reset when:
     * - Value is empty and hour/minute are also empty, OR
     * - Value exists and the converted state matches the prop value
     */
  useEffect(() => {
    if (isInternalSyncRef.current) {
      // If value is empty, sync is complete when hour and minute are empty
      if (!value || !value.includes(':')) {
        if (!hour && !minute) {
          isInternalSyncRef.current = false;
        }
      } else if (hour && minute) {
        // If value exists, sync is complete when state matches the prop
        const hour24 = convertTo24Hour(hour, minute, period);
        if (hour24 === value) {
          isInternalSyncRef.current = false;
        }
      }
    }
  }, [hour, minute, period, value]);

  /**
     * Effect 4: Trigger onChange callback for user-initiated changes
     * Calls the onChange callback when the user makes changes to hour/minute/period.
     * Skips calling onChange during internal syncs (when syncing from props).
     * If user clears the field (hour/minute empty), calls onChange with empty string.
     * Only triggers onChange when the converted 24-hour value differs from the prop.
     */
  useEffect(() => {
    // Skip if this is an internal update from prop sync
    if (isInternalSyncRef.current) {
      return;
    }

    // If hour or minute is empty, call onChange with empty string
    if (!hour || !minute) {
      // Only call onChange if value was not already empty (user cleared the field)
      if (value && value !== '') {
        onChangeRef.current?.({ target: { value: '' } });
      }

      return;
    }

    const hour24 = convertTo24Hour(hour, minute, period);
    if (!hour24) {
      return;
    }

    // Don't call onChange if current state matches the prop (means we just synced from prop)
    if (hour24 === value) {
      return;
    }

    // This is a user-initiated change, call onChange
    onChangeRef.current?.({ target: { value: hour24 } });
  }, [hour, minute, period, value]);

  const handleHourChange = (e) => {
    cancelInternalSync();
    let val = e.target.value.replace(/\D/g, '');

    if (val === '') {
      setHour('');

      return;
    }

    if (val.length > MAX_INPUT_LENGTH) {
      val = val.slice(0, MAX_INPUT_LENGTH);
    }

    const numVal = parseInt(val, 10);
    if (val.length === MAX_INPUT_LENGTH && numVal > HOUR_MAX) {
      if (val[0] === '1') {
        setHour(String(HOUR_MAX));
      } else {
        setHour(val[0]);
      }
    } else {
      setHour(val);
    }
  };

  const handleMinuteChange = (e) => {
    cancelInternalSync();
    let val = e.target.value.replace(/\D/g, '');

    if (val === '') {
      setMinute('');

      return;
    }

    if (val.length > MAX_INPUT_LENGTH) {
      val = val.slice(0, MAX_INPUT_LENGTH);
    }

    const numVal = parseInt(val, 10);
    if (val.length === MAX_INPUT_LENGTH && numVal > MINUTE_MAX) {
      if (val[0] === '5') {
        setMinute(String(MINUTE_MAX));
      } else if (val[0] >= '6') {
        setMinute(val[0]);
      } else {
        setMinute(val);
      }
    } else {
      setMinute(val);
    }
  };

  const handleHourBlur = (e) => {
    if (hour && hour !== '') {
      setHour(validateHour(hour));
    }
    setIsHourFocused(false);
    // Check if focus is moving to another field in this component
    const movingToMinute = e?.relatedTarget === minuteRef.current;
    const movingToPeriod = e?.relatedTarget === periodRef.current;
    // Only deactivate component if focus is leaving entirely
    if (!movingToMinute && !movingToPeriod) {
      setIsComponentActive(false);
    }
  };

  const handleMinuteBlur = (e) => {
    if (minute && minute !== '') {
      setMinute(validateMinute(minute));
    }
    setIsMinuteFocused(false);
    // Check if focus is moving to another field in this component
    const movingToHour = e?.relatedTarget === hourRef.current;
    const movingToPeriod = e?.relatedTarget === periodRef.current;
    // Only deactivate component if focus is leaving entirely
    if (!movingToHour && !movingToPeriod) {
      setIsComponentActive(false);
    }
  };

  const handlePeriodBlur = (e) => {
    setIsPeriodFocused(false);
    // Check if focus is moving to another field in this component
    const movingToHour = e?.relatedTarget === hourRef.current;
    const movingToMinute = e?.relatedTarget === minuteRef.current;
    // Only deactivate component if focus is leaving entirely
    if (!movingToHour && !movingToMinute) {
      setIsComponentActive(false);
    }
  };

  const handlePeriodClick = () => {
    cancelInternalSync();
    setPeriod(period === PERIOD_VALUES.AM ? PERIOD_VALUES.PM : PERIOD_VALUES.AM);
  };

  const handleHourFocus = (e) => {
    cancelInternalSync();
    setIsComponentActive(true);
    setIsHourFocused(true);
    setActiveField(FIELD_TYPES.HOUR);
    if (hourRef.current && hour) {
      const numVal = parseInt(hour, 10);
      if (!Number.isNaN(numVal)) {
        setHour(String(numVal));
      }
    }
    if (hourRef.current && e) {
      e.target.select();
    }
  };

  const handleMinuteFocus = (e) => {
    cancelInternalSync();
    setIsComponentActive(true);
    setIsMinuteFocused(true);
    setActiveField(FIELD_TYPES.MINUTE);
    if (minuteRef.current && minute) {
      const numVal = parseInt(minute, 10);
      if (!Number.isNaN(numVal)) {
        setMinute(String(numVal));
      }
    }
    if (minuteRef.current && e) {
      e.target.select();
    }
  };

  const handlePeriodFocus = () => {
    cancelInternalSync();
    setIsComponentActive(true);
    setIsPeriodFocused(true);
    setActiveField(FIELD_TYPES.PERIOD);
  };

  const handleKeyDown = (e, field) => {
    cancelInternalSync();
    if (field === FIELD_TYPES.HOUR) {
      if (e.key === KEY_CODES.ARROW_UP) {
        e.preventDefault();
        const currentHour = hour && hour !== '' ? parseInt(hour, 10) : HOUR_MIN;
        let numVal = currentHour + 1;
        if (numVal > HOUR_MAX) numVal = HOUR_MIN;
        setHour(String(numVal));
      } else if (e.key === KEY_CODES.ARROW_DOWN) {
        e.preventDefault();
        const currentHour = hour && hour !== '' ? parseInt(hour, 10) : HOUR_MIN;
        let numVal = currentHour - 1;
        if (numVal < HOUR_MIN) numVal = HOUR_MAX;
        setHour(String(numVal));
      } else if (e.key === KEY_CODES.ARROW_RIGHT && !e.shiftKey) {
        const selection = hourRef.current?.selectionStart === hourRef.current?.selectionEnd;
        const cursorPos = hourRef.current?.selectionStart || 0;
        if (selection && cursorPos >= (hour?.length || 0)) {
          e.preventDefault();
          if (minuteRef.current) minuteRef.current.focus();
        }
      }
    } else if (field === FIELD_TYPES.MINUTE) {
      if (e.key === KEY_CODES.ARROW_UP) {
        e.preventDefault();
        const currentMinute = minute && minute !== '' ? parseInt(minute, 10) : MINUTE_MIN;
        let numVal = currentMinute + 1;
        if (numVal > MINUTE_MAX) numVal = MINUTE_MIN;
        setMinute(String(numVal));
      } else if (e.key === KEY_CODES.ARROW_DOWN) {
        e.preventDefault();
        const currentMinute = minute && minute !== '' ? parseInt(minute, 10) : MINUTE_MIN;
        let numVal = currentMinute - 1;
        if (numVal < MINUTE_MIN) numVal = MINUTE_MAX;
        setMinute(String(numVal));
      } else if (e.key === KEY_CODES.ARROW_LEFT) {
        const selection = minuteRef.current?.selectionStart === minuteRef.current?.selectionEnd;
        if (selection && minuteRef.current?.selectionStart === 0) {
          e.preventDefault();
          if (hourRef.current) hourRef.current.focus();
        }
      } else if (e.key === KEY_CODES.ARROW_RIGHT && !e.shiftKey) {
        const selection = minuteRef.current?.selectionStart === minuteRef.current?.selectionEnd;
        const cursorPos = minuteRef.current?.selectionStart || 0;
        if (selection && cursorPos >= (minute?.length || 0)) {
          e.preventDefault();
          if (periodRef.current) periodRef.current.focus();
        }
      }
    } else if (field === FIELD_TYPES.PERIOD) {
      if (e.key === KEY_CODES.ARROW_UP || e.key === KEY_CODES.ARROW_DOWN) {
        e.preventDefault();
        handlePeriodClick();
      } else if (e.key === KEY_CODES.SPACE || e.key === KEY_CODES.ENTER) {
        e.preventDefault();
        handlePeriodClick();
      } else if (e.key === KEY_CODES.ARROW_LEFT) {
        e.preventDefault();
        if (minuteRef.current) minuteRef.current.focus();
      }
    }
  };

  // Display: show raw while focused, padded when not, "--" when empty
  const displayHour = isHourFocused
    ? hour
    : (hour ? String(parseInt(hour, 10)).padStart(2, '0') : '--');

  const displayMinute = isMinuteFocused
    ? minute
    : (minute ? String(parseInt(minute, 10)).padStart(2, '0') : '--');

  const displayPeriod = period;

  return {
    hour,
    minute,
    period,
    activeField,
    displayHour,
    displayMinute,
    displayPeriod,
    hourRef,
    minuteRef,
    periodRef,
    handleHourChange,
    handleMinuteChange,
    handlePeriodClick,
    handleHourFocus,
    handleMinuteFocus,
    handlePeriodFocus,
    handleHourBlur,
    handleMinuteBlur,
    handlePeriodBlur,
    handleKeyDown
  };
};

// eslint-disable-next-line import/prefer-default-export
export { useTimeInput };
